import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { trackWhatsAppClick } from "@/lib/analytics";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Instagram } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const [settings, setSettings] = useState({});
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({ tour_id: "", date: "", guests: 2, name: "", email: "", whatsapp: "", message: "", website: "" });
  const [formStartedAt] = useState(() => Date.now() / 1000);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/tours").then((r) => setTours(r.data || [])).catch(() => {});
  }, []);

  const whatsapp = settings.whatsapp_number || "+96891291423";
  const waLink = `https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`;
  const instagram = settings.instagram_handle || "tour_guide_oman";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tour_id) {
      toast.error("Please select a tour");
      return;
    }
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Please fill in name, email and WhatsApp");
      return;
    }
    if (Date.now() / 1000 - formStartedAt < 3) {
      toast.error(t.form.wait);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        tour_id: form.tour_id,
        date: form.date || "TBD",
        guests: Number(form.guests),
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        message: form.message,
        website: form.website,
        form_started_at: formStartedAt,
      });
      setSent(true);
      toast.success(t.contact_page.sent);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 md:pt-28 pb-20 px-6 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.nav.contact}</p>
          <h1 className="font-serif-display text-4xl md:text-6xl tracking-tight font-light text-charcoal leading-[1.05]">
            {t.contact_page.title}
          </h1>
          <p className="mt-5 text-charcoal-400 text-base md:text-lg">{t.contact_page.sub}</p>

          <div className="mt-8 space-y-4">
            <a href={waLink} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick("contact_details")} className="block group" data-testid="contact-whatsapp-link">
              <p className="text-xs uppercase tracking-widest text-charcoal-400">WhatsApp</p>
              <p className="font-serif-display text-2xl md:text-3xl text-charcoal group-hover:text-terracotta transition">{whatsapp}</p>
            </a>
            <a href={`https://instagram.com/${instagram}`} target="_blank" rel="noreferrer" className="block group" data-testid="contact-instagram-link">
              <p className="text-xs uppercase tracking-widest text-charcoal-400">Instagram</p>
              <p className="font-serif-display text-2xl md:text-3xl text-charcoal group-hover:text-terracotta transition inline-flex items-center gap-2"><Instagram size={22}/> @{instagram}</p>
            </a>
            <div>
              <p className="text-xs uppercase tracking-widest text-charcoal-400">Based in</p>
              <p className="font-serif-display text-2xl md:text-3xl text-charcoal">Muscat, Oman</p>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="bg-sand-100 p-8 md:p-12">
            {sent ? (
              <div data-testid="contact-success">
                <h2 className="font-serif-display text-3xl text-charcoal mb-3">{t.tour.booked_title}</h2>
                <p className="text-charcoal-400">{t.tour.booked_sub}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                  <Label htmlFor="contact-website">Website</Label>
                  <Input
                    id="contact-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
                <p className="rounded-md border border-terracotta/20 bg-terracotta/10 px-4 py-3 text-xs leading-relaxed text-charcoal">
                  {t.form.reassurance}
                </p>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.tour}</Label>
                  <select
                    data-testid="contact-tour"
                    value={form.tour_id}
                    onChange={(e) => setForm({ ...form, tour_id: e.target.value })}
                    className="mt-2 w-full h-10 px-3 rounded-md border border-charcoal/15 bg-white text-charcoal"
                    required
                  >
                    <option value="">—</option>
                    {tours.map((tt) => (
                      <option key={tt.id} value={tt.id}>{tt.title_en}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cdate" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.date}</Label>
                    <Input id="cdate" data-testid="contact-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="cguests" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.guests}</Label>
                    <Input id="cguests" data-testid="contact-guests" type="number" min={1} max={20} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-2 bg-white" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cname" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.name}</Label>
                  <Input id="cname" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white" required />
                </div>
                <div>
                  <Label htmlFor="cemail" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.email}</Label>
                  <Input id="cemail" data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white" required />
                </div>
                <div>
                  <Label htmlFor="cwa" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.whatsapp}</Label>
                  <Input id="cwa" data-testid="contact-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-2 bg-white" required />
                </div>
                <div>
                  <Label htmlFor="cmsg" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.message}</Label>
                  <Textarea id="cmsg" data-testid="contact-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-white" />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="w-full bg-terracotta hover:bg-terracotta-600 disabled:opacity-60 text-sand-50 rounded-full py-3.5 transition"
                >
                  {submitting ? "…" : t.tour.book_btn}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
